import { useContext, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { ContextApp } from "@config/Context/contextApp";
import {
  ClassroomBookingFilterInput,
  OrderByDirection,
  ClassroomBookingOrderByFields,
  ClassroomBookingPage,
  ClassroomBookingStatusEnum,
  Scenario,
} from "@models/ISchema";
import { classroomBookingsCount, classroomBookingsWithoutCount } from "../graphql/bookings.query";

const SIZE_PER_PAGE = 5;

const useGetAppBookings = () => {
  const client = useApolloClient();
  const { user } = useContext(ContextApp);
  const [isLoading, setIsLoading] = useState(false);
  const [bookings, setBookings] = useState<ClassroomBookingPage["items"] | null>(null);
  const [pageInfo, setPageInfo] = useState<ClassroomBookingPage["pageInfo"] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(SIZE_PER_PAGE);
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "expired">("all");
  const [totalActive, setTotalActive] = useState(0);
  const [totalExpired, setTotalExpired] = useState(0);

  const getBookings = async (
    scenario: Scenario,
    page = 1,
    pageSize = sizePerPage,
    statusFilter = activeFilter,
    isWithoutCount = false,
  ) => {
    setIsLoading(true);
    setActiveFilter(statusFilter);

    try {
      const fields: ClassroomBookingFilterInput = {
        fields: {
          and: [
            {
              responsible: {
                id: {
                  eq: parseInt(user?.id),
                },
              },
              isActive:
                statusFilter !== "all"
                  ? {
                      is: statusFilter === "active",
                    }
                  : undefined,
            },
          ],
        },
        orderBy: [
          {
            field: ClassroomBookingOrderByFields.CreatedAt,
            direction: OrderByDirection.Desc,
          },
        ],
        pagination: {
          page,
          size: pageSize,
        },
      };

      const variables = {
        originId: scenario?.productiveOrigin?.id,
        scenarioId: scenario.id,
        filter: fields,
      };

      const { data: countData } = !isWithoutCount
        ? await client.query({
            query: classroomBookingsCount,
            variables,
            fetchPolicy: "no-cache",
          })
        : ({} as any);

      const { data, errors } = await client.query({
        query: classroomBookingsWithoutCount,
        variables,
        fetchPolicy: "no-cache",
      });

      if (errors?.length) {
        console.error("[bookings]", errors);
        setBookings(null);
      }

      const countResponse: ClassroomBookingPage = countData?.cube?.classroomBookings;
      const response: ClassroomBookingPage = data?.cube?.classroomBookings;
      if (response?.items) {
        setBookings(
          response?.items?.filter(
            booking => booking.status !== ClassroomBookingStatusEnum.Cancelled,
          ),
        );
      } else {
        setBookings([]);
      }

      setSizePerPage(pageSize);
      setCurrentPage(response?.pageInfo?.page || page);
      setPageInfo(response?.pageInfo || null);

      if (!isWithoutCount) {
        setTotalActive(countResponse?.activeCount ?? 0);
        setTotalExpired(countResponse?.expiredCount ?? 0);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("[bookings]", error);
      setBookings(null);
    }
  };

  return {
    isLoading,
    bookings,
    pageInfo,
    getBookings,
    sizePerPage,
    currentPage,
    activeFilter,
    totalActive,
    totalExpired,
  };
};

export default useGetAppBookings;
